import {
  Component,
  HostListener,
  Inject,
  OnInit,
  QueryList,
  ViewChildren,
  DOCUMENT
} from '@angular/core';
import { DomSanitizer, Title, SafeHtml } from '@angular/platform-browser';
import { NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@angular-slider/ngx-slider';
import {
  Subject,
  Subscription,
  BehaviorSubject,
  Observable,
  shareReplay,
  forkJoin,
  map
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  CarhireEnterpriseSearchInterface,
  CarHireFilters,
  LocationTypes,
  SortTypesCar,
  TravelPolicies,
  SearchHandler,
  SelectFilter
} from '@sabstravtech/obtservices/base';
import {
  CarHireType,
  type CarHireDetailRequestResponse,
  CarHireAdditionalInfoItem,
  CarHireIdentifiers,
  EnterpriseBasketService,
  HttpCallService,
  ModalOpenerService,
  EnterpriseSearchService,
  UserService,
  type CarHireVehicleAvailibility,
  ServiceType,
  StorageService,
  CurrencyCode,
  type CurrencyRates,
  VehicleType
} from '@sabstravtech/obtservices/angular';
import { Router, ActivatedRoute } from '@angular/router';

import { UsStatesPipe } from '../../../startup/pipes/us-states.pipe';
import { LightningUserFavorurite } from '../../../core/classes/user-favourite.enum';
import { CarLocationsBase } from '../../../core/components/base_components/car-locations-base';
import { resultIcons } from '../../../core/enum/result-icons.enum';
import { DeviceDetector } from '../../../core/services/device-detector.service';
import { Helpers } from '../../../core/classes/helpers';
import { LightningModalTypes } from '../../../core/classes/modal-types.enum';
import { ThemeService } from '../../../core/services/theme.service';
import { CarHireFilterTypes } from '../shared/filter-pills/filter-pills.component';

@Component({
  selector: 'app-cars',
  templateUrl: './cars.component.html',
  styleUrls: ['./cars.component.scss'],
  standalone: false
})
export class CarsComponent extends CarLocationsBase implements OnInit {
  @ViewChildren(NgbInputDatepicker) datepickerList: QueryList<NgbInputDatepicker>;
  ServiceType: typeof ServiceType = ServiceType;
  newSearchSub: Subscription;
  allowNewFilterCreation = true;

  public pickupFocus$ = new Subject<string>();
  public dropoffFocus$ = new Subject<string>();
  public show_errors = false;
  public errorsList: string[] = [];
  public filterClassString = '';
  public notFavourite: number;
  public priceSlidersFocused = false;
  public keyboardPrice: number[];

  public searchParams: CarhireEnterpriseSearchInterface = null;
  public originalSearchDestination = '';
  public pickup_tab = true;
  public sortOption: SortTypesCar = SortTypesCar.cheapest;
  public filterClasses: string[] = [];
  public filterTravelPolicies: string[] = [];
  public filterPreferences: string[] = [];
  public filterChains: string[] = [];
  public filterMakeModels: string[] = [];
  public transmissionStringArray: string[] = [];
  public fuelStringArray: string[] = [];
  public carFuelTypes: string[] = [];
  public options: Options;
  public showMoreClass = false;
  public showSearchForm = false;
  public resultItemType = resultIcons;
  public carTypeCodes: [string, string, CarHireType][];
  public carDetailLoadingStates: BehaviorSubject<boolean>[] = [];
  public carHireAvailabilityDetailsObject: { [key: string]: CarHireDetailRequestResponse } = {};
  results: CarHireVehicleAvailibility[] = [];
  filters: CarHireFilters;
  detailsObject: any = {};
  conversionRates$: Observable<{ [key: string]: CurrencyRates }>;
  preferredCurency: string;
  SortTypesCar = SortTypesCar;
  carTransmissionTypes: string[] = [];

  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent) {
    Helpers.closeOpenCalendars(this.datepickerList, event);
  }
  constructor(
    public deviceDetector: DeviceDetector,
    public searchService: EnterpriseSearchService,
    protected userService: UserService,
    protected modalService: ModalOpenerService,
    protected httpCallService: HttpCallService,
    protected basketService: EnterpriseBasketService,
    protected sanitized: DomSanitizer,
    protected usStatesPipe: UsStatesPipe,
    private storageService: StorageService,
    title: Title,
    public translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    public themeService: ThemeService
  ) {
    super(
      deviceDetector,
      searchService,
      userService,
      modalService,
      httpCallService,
      basketService,
      sanitized,
      usStatesPipe,
      title,
      translateService,
      themeService,
      router
    );
    this.carTypeCodes = this.searchService.searches[ServiceType.Car].carTypeCodes;
  }
  prevLocationType: LocationTypes = LocationTypes.Airport;
  defaultSortingPerformed = false;
  private pendingVendorFilter = '';
  private pendingClassTypeFilter = '';
  private pendingMakeModelFilter = '';

  ngOnInit(): void {
    this.initBaseResults();
    this.setResultsTitle('Car hire results');
    this.searchParams = this.searchService.searches[ServiceType.Car];

    this.subscribe(this.activatedRoute.queryParams, (params) => {
      this.pendingVendorFilter = params['carVendor'] ?? '';
      this.pendingClassTypeFilter = params['carClassType'] ?? '';
      this.pendingMakeModelFilter = params['carMakeModel'] ?? '';
      // Re-apply when params change after results are already loaded (e.g. the
      // user picks a different car in the chat widget while the results page is open).
      if (this.results?.length) {
        this.applyDeepLinkFilters();
      }
    });
    const preferredVendors = this.userService.getUserFavoriteValue(
      LightningUserFavorurite.PreferredCarVendors
    );
    if (preferredVendors) {
      this.notFavourite = preferredVendors.split('|').length + 1;
    }

    this.sortOption =
      this.userService.getUserFavoriteObject<{ sortOrder: SortTypesCar }>(
        LightningUserFavorurite.CarHireResultsConfiguration
      )?.sortOrder || SortTypesCar.cheapest;

    this.subscribe(this.searchParams.results, (results: CarHireVehicleAvailibility[]) => {
      const uniqueCurrencyCodes = results.reduce((uniqueSet, result) => {
        (result?.displayRecord?.charges || []).forEach((charge) => {
          if (charge.currencyCode) {
            uniqueSet.add(charge.currencyCode); // Add only unique values
          }
        });
        return uniqueSet;
      }, new Set());

      const uniqueCurrencyArray: CurrencyCode[] = Array.from(uniqueCurrencyCodes) as CurrencyCode[]; // Convert Set to Array

      console.log(uniqueCurrencyArray);
      this.conversionRates$ = forkJoin(
        uniqueCurrencyArray.map((currencyCode) =>
          this.searchService
            .getCurrencyConversionRates({ baseCurrencyCode: currencyCode })
            .pipe(map((response) => ({ currencyCode, rates: response })))
        )
      ).pipe(
        map((responses) =>
          responses.reduce((acc, { currencyCode, rates }) => {
            acc[currencyCode] = rates;
            return acc;
          }, {})
        )
      );
      console.log(results);
      this.results = results;
      this.showSearchForm = false;
      this.setCarDetailLoadingStates(this.results.length);
      this.setOriginalSearchDestination();
      this.results.forEach((result, i) => {
        this.detailsObject[i] = false;
      });
      this.prevLocationType = this.searchParams.locationType;

      this.createFilters(results);
      this.applyDeepLinkFilters();
    });

    const preferredCurencyObject = this.userService.getUserFavoriteObject<{
      carhire: { enable: boolean };
      currency: string;
    }>(LightningUserFavorurite.PreferredCurrency);

    if (
      preferredCurencyObject &&
      preferredCurencyObject.carhire?.enable &&
      preferredCurencyObject.currency
    ) {
      this.preferredCurency = preferredCurencyObject.currency;
    }
  }

  formatter_rail_air = (x: { destination: string }) => x.destination; // used for formatting the output on the dropdown

  formatter = (x: { Name: string; Display?: string }) => (x.Display ? x.Display : x.Name);
  postcodeFormatter = (x: { name: string }) => x.name;

  cityFormatter = (x: { name: string; countryCode: string; admin1_code: string }) =>
    x.name + '(' + x.countryCode + ')';

  switchTab(): void {
    this.pickup_tab = !this.pickup_tab;
  }

  setOriginalSearchDestination(): void {
    switch (this.searchParams.locationType) {
      case LocationTypes.Airport:
        this.originalSearchDestination = `
          ${this.searchParams.pickup_location?.destination}
          (${this.searchParams.pickup_location?.gateway}),
          ${this.searchParams.pickup_location?.country}
        `;
        break;
      case LocationTypes.City:
        this.originalSearchDestination = this.searchParams?.cityPickupDepot
          ? this.searchParams?.cityPickupDepot?.addressLines[0]
          : this.searchParams.cityPickup?.name;
        break;
      case LocationTypes.Postcode:
        this.originalSearchDestination = this.searchParams?.postcodeDropoff
          ? this.searchParams?.postcodeDropoff?.addressLines[0]
          : this.searchParams.postcodePickupRaw?.name;
        break;
      default:
        console.warn(
          `Unhandled location type: '${this.searchParams.locationType}' in setOriginalSearchDestination()`
        );
    }
  }

  /**
    @desc - sets the carDetailLoadingStates array of BehaviourSubject's to false, 1 array element for each result.
    @param numResults for how many array elements to add
  **/
  setCarDetailLoadingStates(numResults: number): void {
    this.carDetailLoadingStates = [];
    for (let i = 0; i < numResults; i++) {
      this.carDetailLoadingStates.push(new BehaviorSubject(false));
    }
  }
  reSearch(): void {
    let prechosehotel = false;
    if (this.searchService.search_objects[ServiceType.Hotel].chosen) {
      this.searchService.search_objects[ServiceType.Hotel].chosen = false;
      prechosehotel = true;
    }

    this.searchService.search_objects[ServiceType.Car].chosen = true;
    if (this.searchService.validateForms()) {
      this.clearFilters();
      this.searchService.determineHighestSearchPriority();
      this.searchService.search_objects[ServiceType.Hotel].chosen = prechosehotel;
      this.searchService.startSearches();
    } else {
      this.show_errors = true;
    }
  }

  closeValidationErrors(): void {
    this.show_errors = false;
  }

  sliderOptions(): Options {
    return {
      floor: Math.floor(this.searchService.searches[ServiceType.Car].getMinPrice(this.results)),
      ceil: Math.ceil(this.searchService.searches[ServiceType.Car].getMaxPrice(this.results)),
      step: 1,
      ariaLabel: 'Minimum car hire price',
      ariaLabelHigh: 'Maximum car hire price'
    };
  }

  /**
    @desc - resort the results either by price (cheapest first) or by brand (alphabetical)
  **/
  sortBy(sortType: SortTypesCar): void {
    this.defaultSortingPerformed = true;
    this.sortOption = <SortTypesCar>sortType.toLowerCase();

    switch (this.sortOption) {
      case SortTypesCar.cheapest:
        this.searchService.searches[ServiceType.Car].sortCheapest();
        break;
      case SortTypesCar.chain:
        this.searchService.searches[ServiceType.Car].sortByBrand();
        break;
      case SortTypesCar.expensive:
        // ! added because go2
        this.searchService.searches[ServiceType.Car].sortExpensive();
        break;
      case SortTypesCar.size:
        this.searchService.searches[ServiceType.Car].sortBySize();
        break;
      case SortTypesCar.lowestEmissions:
        this.searchService.searches[ServiceType.Car].sortByCo2();
        break;
      case SortTypesCar.preferred:
        // ! to implement this will become direct method
        this.searchService.searches[ServiceType.Car].sortResults(2);
        break;
      default:
        // console.log(sortType);
        console.log('Unknown sort type!');
    }
    this.showSortOptions = false;
  }

  /**
    @desc - filter the class according to the type passed in
  **/
  filterClass(type: string): void {
    const index = this.filterClasses.indexOf(type);
    if (index >= 0) {
      this.filterClasses = this.filterClasses
        .slice(0, index)
        .concat(this.filterClasses.slice(index + 1));
    } else {
      this.filterClasses = [...this.filterClasses, type];
    }
  }

  filterChain(chain: string): void {
    const index = this.filterChains.indexOf(chain);
    if (index >= 0) {
      this.filterChains = this.filterChains
        .slice(0, index)
        .concat(this.filterChains.slice(index + 1));
    } else {
      this.filterChains = [...this.filterChains, chain];
    }
  }

  filterTransmission(trans: string): void {
    console.log('attempting to filter transmissions!');
    const index = this.transmissionStringArray.indexOf(trans);
    if (index >= 0) {
      this.transmissionStringArray = this.transmissionStringArray.filter(
        (tranType, i) => i !== index
      );
    } else {
      this.transmissionStringArray = [...this.transmissionStringArray, trans];
    }
  }

  filterFuel(fuel: string): void {
    const index = this.fuelStringArray.indexOf(fuel);
    if (index >= 0) {
      this.fuelStringArray = this.fuelStringArray.filter((fuel, i) => i !== index);
    } else {
      this.fuelStringArray = [...this.fuelStringArray, fuel];
    }
  }

  filterTravelPolicy(type: string): void {
    const index = this.filterTravelPolicies.indexOf(type);
    if (index >= 0) {
      this.filterTravelPolicies = this.filterTravelPolicies
        .slice(0, index)
        .concat(this.filterTravelPolicies.slice(index + 1));
    } else {
      this.filterTravelPolicies = [...this.filterTravelPolicies, type];
    }
  }

  filterPreference(type: string): void {
    const index = this.filterPreferences.indexOf(type);
    if (index >= 0) {
      this.filterPreferences = this.filterPreferences
        .slice(0, index)
        .concat(this.filterPreferences.slice(index + 1));
    } else {
      this.filterPreferences = [...this.filterPreferences, type];
    }
  }

  automaticFilterPreference(type: string): void {
    const index = this.filterPreferences.indexOf(type);
    if (index >= 0) {
      // If it already exists, do nothing
    } else {
      this.filterPreferences = [...this.filterPreferences, type];
    }
  }

  matchesFilterClassTypeString(classType: string): boolean {
    const index = this.filterClasses.indexOf(classType);
    return !!(this.filterClasses.length === 0 || index >= 0);
  }

  matchesTransmission(transmissionType: string): boolean {
    const index = this.transmissionStringArray.indexOf(transmissionType?.toLowerCase());
    return !!(this.transmissionStringArray.length === 0 || index >= 0);
  }

  matchesTravelPolicy(result: CarHireVehicleAvailibility): boolean {
    const requiresReason = result.unavailable
      ? TravelPolicies.UNAVAILABLE
      : result.requiresReasonKeys.length
        ? TravelPolicies.ALLOW_WITH_REASON
        : TravelPolicies.IN_POLICY;

    if (this.filterTravelPolicies.length === 0) {
      return true;
    }

    // needs sorting
    const filteredTravelPolicies = this.filterTravelPolicies.find(
      (policy: string) => policy.toString().toLowerCase() === requiresReason.toLowerCase()
    );
    if (filteredTravelPolicies) {
      return true;
    }

    return false;
  }

  matchesPreference(result: CarHireVehicleAvailibility): boolean {
    if (this.filterPreferences.length === 0) {
      return true;
    }

    const preferences = this.filterPreferences.map((pref) => pref.toString().toLowerCase());

    if (preferences.includes(SearchHandler.ANY.toLowerCase())) {
      return true;
    }

    if (
      preferences.includes(SearchHandler.CLIENT.toLowerCase()) &&
      this.isClientPreferred(result)
    ) {
      return true;
    }

    return false;
  }

  matchesFuel(type: string): boolean {
    const index = this.fuelStringArray.indexOf(type);
    return !!(this.fuelStringArray.length === 0 || index >= 0);
  }

  inRange(price: number | string): boolean {
    const priceVal = Number(price);

    if (priceVal >= this.filters.minPrice && priceVal <= this.filters.maxPrice) {
      return true;
    }

    return false;
  }

  matchesChainFilter(vendor: string): boolean {
    const index = this.filterChains.indexOf(vendor);
    if (this.filterChains.length === 0) {
      return true;
    }
    if (index >= 0) {
      return true;
    }
    return false;
  }

  inFilters(result: CarHireVehicleAvailibility): boolean {
    let inFilters = true;
    const vehicle = result.displayRecord.vehicle;
    const makeModel =
      result.displayRecord?.vehicle?.description || result.identifiers?.makeModel || '';
    if (
      this.inRange(result.displayRecord.charges[0].amount) &&
      this.matchesTransmission(vehicle.transmission) &&
      this.matchesTravelPolicy(result) &&
      this.matchesFilterClassTypeString(`${vehicle.class}_${vehicle.type}`) &&
      this.matchesChainFilter(result.displayRecord.vendor.name) &&
      (this.filterMakeModels.length === 0 || this.filterMakeModels.includes(makeModel)) &&
      this.matchesFuel(vehicle.fuel) &&
      this.matchesPreference(result)
    ) {
    } else {
      inFilters = false;
    }
    return inFilters;
  }

  clearFilters(): void {
    this.filterClasses = [];
    this.filterTravelPolicies = [];
    this.filterPreferences = [];
    this.filterChains = [];
    this.filterMakeModels = [];
    this.transmissionStringArray = [];
    this.fuelStringArray = [];
    // ! set max and min prices to some sort of variable here
    this.filters.minPrice = this.sliderOptions().floor;
    this.filters.maxPrice = this.sliderOptions().ceil;
  }

  closeFiltersOptions(): void {
    this.closeFilterOptions();
    this.ariaToggleHideBackground();
  }

  /**
  @desc - applies vendor / class-type filters passed via query params (e.g. when a
  car is selected from the chat assistant) so the results page narrows to that car.
  **/
  private applyDeepLinkFilters(): void {
    if (this.pendingVendorFilter) {
      this.filterChains = [this.pendingVendorFilter];
    } else {
      this.filterChains = [];
    }
    if (this.pendingClassTypeFilter) {
      this.filterClasses = [this.pendingClassTypeFilter];
    } else {
      this.filterClasses = [];
    }
    if (this.pendingMakeModelFilter) {
      this.filterMakeModels = [this.pendingMakeModelFilter];
    } else {
      this.filterMakeModels = [];
    }
  }

  /**
  @desc - sets up filters in a new way using new obtservices, still filtering the old way
  @param - all searrch results
  **/
  createFilters(allResults: CarHireVehicleAvailibility[]): void {
    const newFilters: CarHireFilters = {
      minPrice: Math.floor(this.searchService.searches[ServiceType.Car].getMinPrice(allResults)),
      maxPrice: Math.ceil(this.searchService.searches[ServiceType.Car].getMaxPrice(allResults)),
      transmissions: [
        {
          display: 'Automatic',
          value: 'A',
          selected: false
        },
        {
          display: 'Manual',
          value: 'M',
          selected: false
        }
      ],
      travelPolicy: [
        {
          display: TravelPolicies.IN_POLICY,
          value: TravelPolicies.IN_POLICY,
          selected: false
        },
        {
          display: TravelPolicies.ALLOW_WITH_REASON,
          value: TravelPolicies.ALLOW_WITH_REASON,
          selected: false
        }
      ]
    };

    // Add preferences based on available car results
    this.addPreferenceFilters(allResults, newFilters);

    const carTypes = {};
    this.carFuelTypes = [];
    try {
      allResults.forEach((car) => {
        const vehicle = car.displayRecord.vehicle;
        const filterValue = `${vehicle.class}_${vehicle.type}`;
        const filterDisplay = `${this.translateService.instant(vehicle.class)} ${this.translateService.instant(this.getTypeForDisplayFromResult(car))}`;
        carTypes[filterValue] = {
          selected: false,
          value: filterValue,
          display: filterDisplay
        };

        if (this.carFuelTypes.indexOf(vehicle.fuel) === -1) {
          this.carFuelTypes.push(vehicle.fuel);
        }
        if (this.carTransmissionTypes.indexOf(vehicle.transmission) === -1) {
          this.carTransmissionTypes.push(vehicle.transmission);
        }
      });
    } catch (error) {
      console.error(`+++ Error generating car types filters: ${error} +++`);
    }

    const carCompanies = {};
    const baseUrl = 'https://images.sabscorp.com/images/carhire';
    try {
      allResults.forEach((car) => {
        carCompanies[car.identifiers.vendor] = {
          selected: false,
          value: car.displayRecord.vendor.logo || `${baseUrl}/${car.identifiers.vendor}.gif`,
          display: car.displayRecord.vendor.name
        };
      });
    } catch (error) {
      console.error(`+++ Error generating car companies filters: ${error} +++`);
    }

    newFilters.types = Object.values(carTypes);
    newFilters.companies = Object.values(carCompanies);

    newFilters.companies = newFilters.companies.sort((a, b) => {
      if (a.display < b.display) {
        return -1;
      }
      if (a.display > b.display) {
        return 1;
      }
      return 0;
    });
    this.filters = newFilters;
  }

  private addPreferenceFilters(
    allResults: CarHireVehicleAvailibility[],
    newFilters: CarHireFilters
  ): void {
    const preferenceOptions: SelectFilter[] = [];

    const hasPreferences = allResults.some((result) => {
      if (this.isClientPreferred(result)) {
        preferenceOptions.push({
          display: SearchHandler.CLIENT,
          value: SearchHandler.CLIENT,
          selected: false
        });
        return true;
      }
      return false;
    });

    if (hasPreferences) {
      // Don't want to display "Any" filter as per 11931
      // preferenceOptions.unshift({ display: SearchHandler.ANY, value: SearchHandler.ANY, selected: false });
      newFilters.preference = preferenceOptions;
      const automaticallySelectPreference = this.userService.getUserFavoriteObject<{
        defaultFilters: { negotiatedRatesClient: boolean };
      }>(LightningUserFavorurite.CarHireResultsConfiguration)?.defaultFilters
        ?.negotiatedRatesClient;
      if (automaticallySelectPreference) {
        this.automaticFilterPreference(SearchHandler.CLIENT);
      }
    }
  }

  skipToResultContent(): boolean {
    return super.skipToResultContent('car-list');
  }

  trapFocusReverse(event: KeyboardEvent): void {
    super.trapFocusReverse(event, 'filters');
  }

  trapFocus(event: KeyboardEvent, i: number, array: unknown[]): void {
    super.trapFocus(event, i, array, 'filters');
  }

  ariaToggleHideBackground(): void {
    super.ariaToggleHideBackground('car-hire-filters-wrapper', this.showFilterOptions);
  }

  addAriaLabels() {
    if (this.showFilterOptions) {
      setTimeout(function () {
        const sliders = document.querySelectorAll('[role="slider"]');
        for (let x = 0; x < sliders.length; x++) {
          if (x === 0) {
            sliders[x].setAttribute('aria-label', 'Price Range Lower Bound');
            sliders[x].setAttribute('tabindex', '-1');
          }
          if (x === 1) {
            sliders[x].setAttribute('aria-label', 'Price Range Higher Bound');
            sliders[x].setAttribute('tabindex', '-1');
          }
        }
      }, 0);
    }
  }

  keyboardCheckBound(value, sliderMin, sliderMax) {
    return !(value >= sliderMin && value <= sliderMax);
  }

  /**
    @desc - sets the showSearchForm property according to the state
    @param state boolean representing the current state
  **/
  toggleSearchForm(state: boolean): void {
    this.showSearchForm = state;
  }

  /**
    @desc - for a given result return the type string for vanity purposes for a vehicle (utilising carTypeCodes in obtservices)
    @param result of type CarHireVehicleAvailibility
    @returns the car type string e.g. '2-3 Door' instead of 'TwoToThreeDoor' raw type
  **/
  getTypeForDisplayFromResult(result: CarHireVehicleAvailibility): string {
    const typeForDisplay = this.carTypeCodes.find(
      (carTypeCode) => carTypeCode[2] === result?.displayRecord?.vehicle?.type
    );
    if (typeForDisplay) {
      return typeForDisplay[1];
    }
  }

  /**
    @desc - check if a key exists in the carHireAvailabilityDetailsObject
    @return boolean
  **/
  carHireAvailabilityDataExists(key: string): boolean {
    return key in this.carHireAvailabilityDetailsObject;
  }

  get dropoffCountry() {
    switch (this.prevLocationType) {
      case LocationTypes.City:
        return this.searchParams.cityDropoff.countryCode;
      case LocationTypes.Airport:
        return this.searchParams.dropoff_country;
      case LocationTypes.Postcode:
        return this.searchParams.postcodeDropoff.countryCode;
    }
  }

  switchToHotel(): void {
    // Build location based on car location type
    let location:
      | {
          name?: string;
          latitude?: number;
          longitude?: number;
          countryCode?: string;
          type?: LocationTypes;
        }
      | undefined;

    if (
      this.searchParams.locationType === LocationTypes.Airport &&
      this.searchParams.pickup_location
    ) {
      const pickupLoc = this.searchParams.pickup_location;
      location = {
        name: pickupLoc.destination || pickupLoc.gateway,
        latitude: pickupLoc.latitude,
        longitude: pickupLoc.longitude,
        countryCode: pickupLoc.countryCode,
        type: LocationTypes.Airport
      };
      // Also set country for hotel search
      if (this.searchParams.pickup_country) {
        this.searchService.searches[ServiceType.Hotel].country = {
          cCode: this.searchParams.pickup_country,
          cName: this.searchParams.pickup_country
        };
      }
    } else if (
      this.searchParams.locationType === LocationTypes.City &&
      this.searchParams.cityPickup
    ) {
      location = this.searchParams.cityPickup;
    }

    this.navigateToHotelSearch(
      ServiceType.Car,
      {
        checkin: this.searchParams.pickup_datetime,
        checkout: this.searchParams.dropoff_datetime
      },
      location
    );
  }

  setFocus(string) {
    setTimeout(() => {
      this.document.getElementById(string).focus();
    }, 0);
  }

  private isClientPreferred(car: CarHireVehicleAvailibility): boolean {
    return car.fareInfo.type === VehicleType.Private;
  }

  carHireRemoveFilter(args: { filterType: string; filter: string }) {
    const index = this[args.filterType].indexOf(args.filter);
    if (index >= 0) {
      this[args.filterType] = this[args.filterType]
        .slice(0, index)
        .concat(this[args.filterType].slice(index + 1));
    }
  }
}
